<template>
  <div class="mb-4">
    <ContextMenuList
      :options="
        filterOptions.map((opt) => ({
          name: opt.text,
          action: () => (filter = opt.value),
        }))
      "
    >
      <template v-slot="{ isOpen, open, close }">
        <NamiButton
          small
          button-type="secondary"
          :disabled="pending"
          @button-click="() => (isOpen ? close() : open())"
        >
          <span class="px-1 flex items-center gap-1">
            {{ $t(`site.user.id.achievements.filter.${filter}`) }}
            <IconChevronDown />
          </span>
        </NamiButton>
      </template>
    </ContextMenuList>
  </div>
  <Card v-if="pending">
    <NamiLoading />
  </Card>
  <Card v-else-if="!computedProgresses.length">
    <NoResults
      :message="$t(`site.user.id.achievements.no_results.${filter}`)"
    />
  </Card>
  <div v-else-if="data" class="space-y-4">
    <AchievementProgressCard
      v-for="ach in computedProgresses"
      :progress="ach"
      :achievement="
        data.achievementMap[ach.relationships.find(isAchievement)?.id ?? '']
      "
      :hide-award-indicator="authUserId !== user.id"
    />
    <NamiPagination
      v-model="page"
      :total-pages="Math.ceil((data?.progresses.meta.total ?? 0) / limit)"
      keep-route
    />
  </div>
</template>

<script setup lang="ts">
import {
  Achievement,
  type AchievementEntity,
  type AchievementMetric,
  type AchievementProgressEntity,
  isAchievement,
  isExpanded,
  type UserAchievementProgressListParams,
  type UserEntity,
} from "~/src/api";
import {
  getCreatorMetrics,
  getSystemMetrics,
  getUserMetrics,
} from "~/src/forms/achievement";
import { IconChevronDown } from "@tabler/icons-vue";

const nuxtApp = useNuxtApp();
const authStore = nuxtApp.$auth();
const { t } = nuxtApp.$i18n.global;
const authUserId = computed(() => authStore?.user?.profile.sub);
const userMetrics = getUserMetrics() as AchievementMetric[];

const globalMetrics = getCreatorMetrics().concat(getSystemMetrics());
const creatorMetrics = getUserMetrics();

const filterMap: Record<string, UserAchievementProgressListParams> = {
  aa: {},
  ga: {
    metrics: globalMetrics,
  },
  ca: {
    metrics: creatorMetrics,
  },
  ia: {
    showAwarded: false,
  },
};
const filterOptions = ["aa", "ga", "ca", "ia"].map((opt) => ({
  text: t(`site.user.id.achievements.filter.${opt}`),
  value: opt,
}));

const filter = ref("aa");

const { user } = defineProps<{
  user: UserEntity;
}>();

const { limit, offset, page } = useInternalPagination();

const { data, pending } = useAsyncData(
  async () => {
    const progresses = await Achievement.getUserAchievementProgresses(user.id, {
      includes: ["badge", "achievement"],
      order: { awardedAt: "desc" },
      limit: limit.value,
      offset: offset.value,
      ...(filterMap[filter.value] ?? {}),
    });

    const achievementsToBeFetched = progresses.data
      .filter((res) => {
        const ach = res.relationships.find(isAchievement);
        if (!ach?.attributes?.metric) return false;
        return userMetrics.includes(ach.attributes.metric);
      })
      .map((res) => res.relationships.find(isAchievement)?.id)
      .filter(onlyTruthys);

    if (!achievementsToBeFetched.length)
      return {
        progresses,
        achievementMap: {},
      };

    // we dont need to full paginate this because the max length will be limited but the progress fetch call
    const achievements = await Achievement.find({
      ids: achievementsToBeFetched,
      includes: ["organization"],
      limit: achievementsToBeFetched.length,
    });
    const achievementMap = Object.fromEntries(
      achievements.data.map((ach) => [ach.id, ach]),
    ) as Record<string, AchievementEntity>;

    return {
      progresses,
      achievementMap,
    };
  },
  { watch: [page, filter] },
);

const filterHidden = (prog: AchievementProgressEntity) => {
  const ach = prog.relationships.find(isAchievement);
  if (!ach || !isExpanded(ach)) return false;

  if (user.id === authUserId.value && prog.attributes.targetReached)
    return true;
  if (prog.attributes.progressValue === 0) return false;
  return !ach.attributes.isHidden;
};

const computedProgresses = computed(
  () => data.value?.progresses.data.filter(filterHidden) ?? [],
);
</script>
